import { usePaymentContext } from 'features/payment';
import { CASH_ACCOUNT_IDENTIFIER } from 'features/payment/constants';
import { isApplePay, isCashApp, isGooglePay, isPayPal, isVenmo } from 'features/payment/utils';
import { useStoreContext } from 'features/store';

import { ILegacyCartEntry } from '@rbi-ctg/menu';
import { useDistance } from 'hooks/geolocation/use-distance';
import { useIsVehicleDescriptionEnabled } from 'pages/cart/hooks/use-is-vehicle-description-enabled';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import useRewards from 'state/rewards/use-rewards';
import { useServiceModeContext } from 'state/service-mode';
import { ISOs } from 'utils/form/constants';

import { useCheckoutState } from '../checkout-provider';
import { CheckoutContext } from '../types';

export const useCheckoutContext = (): CheckoutContext => {
  const enableGiftCard = useFlag(LaunchDarklyFlag.ENABLE_GIFT_CARD);

  const { user } = useAuthContext();
  const { email: storeEmail, store } = useStoreContext();
  const [checkoutState, setCheckoutState] = useCheckoutState();

  const { serverOrder, vehicleDescription } = checkoutState;
  const { cart } = serverOrder ?? {};

  const cartEntries = ((cart?.cartEntries ?? []) as unknown) as ILegacyCartEntry[];
  const isVehicleDescriptionEnabled = useIsVehicleDescriptionEnabled();
  const { isDelivery } = useServiceModeContext();
  const {
    checkoutPaymentMethodId,
    checkoutPaymentMethod,
    paymentMethods,
    reloadPaymentValues,
    canUseApplePay,
    canUseGooglePay,
    isFirstData,
    isOrbital,
    paymentDetails,
    prepaidReloadPaymentMethodId,
    loading: isLoadingPaymentMethods,
    savingPaymentMethods,
    encryptionResult: orbitalEncryptionResult,
    getPrepaidPaymentMethod,
  } = usePaymentContext();

  const billingCountry = (user?.details?.isoCountryCode as ISOs) || ISOs.USA;

  const prepaidPaymentMethod = getPrepaidPaymentMethod();
  const accountIdentifier =
    prepaidPaymentMethod?.accountIdentifier ?? prepaidPaymentMethod?.fdAccountId ?? '';
  const isPrepaidPaymentMethodSelected =
    !!accountIdentifier && checkoutPaymentMethodId === accountIdentifier;

  const { totals } = checkoutState;

  const { reloadAmount } = useRewards({
    prepaidCard: prepaidPaymentMethod,
    serverOrder,
    tipAmount: totals?.tipAmount ?? 0,
    user,
  });

  const showReloadPaymentMethods =
    enableGiftCard && isPrepaidPaymentMethodSelected && reloadAmount > 0;

  const isPayPalPayment = isPayPal(checkoutPaymentMethod?.accountIdentifier);
  const isVenmoPayment = isVenmo(checkoutPaymentMethod?.accountIdentifier);
  const isCashAppPayment = isCashApp(checkoutPaymentMethod?.accountIdentifier);
  const isGooglePayPayment = isGooglePay(checkoutPaymentMethod?.accountIdentifier);
  const isApplePayPayment = isApplePay(checkoutPaymentMethod?.accountIdentifier);
  const isPrepaidPayment = !!checkoutPaymentMethod?.prepaid;
  const isOneTimePaymentFlowEnabled = useFlag(
    LaunchDarklyFlag.ENABLE_VAULT_AFTER_MAKING_ONE_TIME_PAYMENT
  );

  const distanceToStore = useDistance({
    restaurant: store,
    shouldUseUserCoordinatesIfAvailable: true,
  });

  return {
    ...checkoutState,

    storeEmail,
    distanceToStore,

    // Payment specific
    billingCountry,
    paymentMethods,
    checkoutPaymentMethodId,
    checkoutPaymentMethod,

    prepaidReloadPaymentMethodId,

    showReloadPaymentMethods,
    paymentValues: paymentDetails.state,
    reloadPaymentValues,
    reloadAmount,
    orbitalEncryptionResult,
    isOneTimePaymentFlowEnabled,

    canUseApplePay,
    canUseGooglePay,

    isPaymentProcessor: {
      firstData: isFirstData,
      orbital: isOrbital,
      braintree: isPayPalPayment || isVenmoPayment,
    },
    isPaymentType: {
      cash: checkoutPaymentMethod?.accountIdentifier === CASH_ACCOUNT_IDENTIFIER,
      cashApp: isCashAppPayment,
      paypal: isPayPalPayment,
      venmo: isVenmoPayment,
      googlePay: isGooglePayPayment,
      applePay: isApplePayPayment,
      prepaid: isPrepaidPayment,
    },

    // Cart specific
    serverOrder,
    serviceMode: cart?.serviceMode ?? null,
    isDeliveryOrder: isDelivery,
    cart,
    cartEntries,
    isVehicleDescriptionEnabled,
    vehicleDescription,

    // load state
    isLoadingPaymentMethods: isLoadingPaymentMethods || savingPaymentMethods,

    setCheckoutState,
  };
};
